import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { usePiprChart } from '../api/usePiprChart';
import { minuteNow } from '../pipr/ranges';
import type { BankInfo } from '../pipr/types';
import { PiprCard } from './PiprCard';
import { PageFrame } from '../../../components/common/page/PageFrame';
import { PageHeader } from '../../../components/common/page/PageHeader';

const CARD_SPAN_MS = 90 * 86_400_000;
const DEFAULT_BANK = 'fed';

export function MonitorOverview() {
    const [span] = useState(() => {
        const to = minuteNow();
        return { from: new Date(to - CARD_SPAN_MS), to: new Date(to) };
    });
    const request = useMemo(
        () => ({
            bank: DEFAULT_BANK,
            from: span.from,
            to: span.to,
            resolution: 1440,
        }),
        [span]
    );
    const { data } = usePiprChart(request);
    const banks: readonly BankInfo[] = data?.banks ?? [];
    const list = banks.length > 0 ? banks.map(b => b.bank) : [DEFAULT_BANK];

    return (
        <PageFrame>
            <PageHeader
                title="Monitor"
                meta={
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            maxWidth: 760,
                        }}
                    >
                        Indicators that we build from many prediction markets.
                        Each number links to the markets behind it.
                    </Typography>
                }
            />

            <Box>
                <Typography
                    sx={{ fontSize: '13px', fontWeight: 700, mb: 0.25 }}
                >
                    PIPR · implied policy rates
                </Typography>
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: 'text.secondary',
                        mb: 1.25,
                        maxWidth: 760,
                    }}
                >
                    The policy rate that the prediction markets expect after the
                    next central bank meeting, from the probabilities of each
                    possible decision.
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 2,
                        maxWidth: 1200,
                    }}
                >
                    {list.map(bank => (
                        <PiprCard
                            key={bank}
                            bank={bank}
                            info={banks.find(b => b.bank === bank) ?? null}
                            from={span.from}
                            to={span.to}
                        />
                    ))}
                </Box>
            </Box>
        </PageFrame>
    );
}
